import type { ReactNode } from 'react';
import { CalendarBlankIcon, MapPinIcon } from '@phosphor-icons/react';
import {
  formatClockTime,
  formatDateRange,
  formatDateTimeWindow,
  parseLocalDateTime,
  startOfDay,
} from '@voll-intelligence/dates';
import { PersonAvatar } from '../../shared/person/person_row.js';
import {
  SummaryPill,
  type SummaryPillSegment,
  type SummaryPillTarget,
} from '../../shared/summary_pill/summary_pill.js';

/**
 * A pill de resumo da busca de CARRO, a MESMA na página do web-chat e no
 * takeover "Ver mais opções" da conversa: condutor, lugar(es) e a janela da
 * locação. Na página o pedido é da pessoa (condutor do `/me`, lupa, clique
 * volta ao formulário); na conversa é do agente (sem condutor, `readOnly`).
 */

export type RentalSummary = {
  pickup: string;
  /** A devolução, quando em outro lugar: a pill mostra "Retirada → Devolução". */
  dropoff?: string;
  /** `YYYY-MM-DDTHH:mm`, a hora do balcão. Sem as duas, a pill fica sem janela. */
  pickupAt?: string;
  dropoffAt?: string;
  driver?: { name: string; photoUrl?: string | null };
};

export type RentalSummaryPillCopy = {
  aria: string;
  /** A segunda linha do celular quando o condutor ainda não tem nome. Ausente,
   *  a linha fica só com os horários. */
  driverPlaceholder?: string;
};

export type RentalSummaryPillProps = {
  summary: RentalSummary;
  copy: RentalSummaryPillCopy;
  locale: string;
  readOnly?: boolean;
  onExpand?: (target: SummaryPillTarget) => void;
  /** A lupa e o nome dela, como na `SummaryPill`. Ausente ⇒ sem lupa. */
  search?: { onSearch: () => void; label: string };
};

type RentalWindow = { label: string; days: string; hours: string };

/** A janela inteira ("21 out, 10:00 – 24 out, 18:30") e partida para o celular:
 *  os dias na régua das pills ("21–24 out") e os horários ("10:00 – 18:30"). */
function rentalWindow(summary: RentalSummary, locale: string): RentalWindow | null {
  const from = parseLocalDateTime(summary.pickupAt);
  const to = parseLocalDateTime(summary.dropoffAt);
  if (!from || !to) return null;
  const today = new Date();
  return {
    label: formatDateTimeWindow(from, to, locale, today),
    days: formatDateRange(startOfDay(from), startOfDay(to), locale, today),
    hours: `${formatClockTime(from, locale)} – ${formatClockTime(to, locale)}`,
  };
}

/** Condutor, lugar(es) e janela — cada um só quando o pedido o diz. */
function rentalSegments(
  driver: RentalSummary['driver'],
  places: string,
  window: RentalWindow | null,
  locale: string,
): SummaryPillSegment[] {
  return [
    ...(driver
      ? [
          {
            key: 'traveler' as const,
            // O condutor também não encolhe (#4513, L2): entre 900 e 1280 px ele
            // virava "Vi…" enquanto o lugar, que se lê pelo começo, seguia inteiro.
            firm: true,
            icon: (
              <PersonAvatar
                name={driver.name}
                photoUrl={driver.photoUrl ?? null}
                size="xs"
                locale={locale}
              />
            ),
            label: driver.name,
          },
        ]
      : []),
    ...(places
      ? [{ key: 'destination' as const, icon: <MapPinIcon size={14} aria-hidden />, label: places }]
      : []),
    // A janela fica inteira; quando a barra aperta, é o lugar que ganha
    // reticências — ele ainda se lê pelo começo, a janela sem o fim não (#3608).
    ...(window
      ? [
          {
            key: 'dates' as const,
            firm: true,
            icon: <CalendarBlankIcon size={14} aria-hidden />,
            label: window.label,
          },
        ]
      : []),
  ];
}

/** A pill, ou nada quando o pedido não diz nem lugar nem janela. */
export function RentalSummaryPill({
  summary,
  copy,
  locale,
  readOnly,
  onExpand,
  search,
}: Readonly<RentalSummaryPillProps>): ReactNode {
  const { driver } = summary;
  const places =
    summary.dropoff && summary.dropoff !== summary.pickup
      ? `${summary.pickup} → ${summary.dropoff}`
      : summary.pickup;
  const window = rentalWindow(summary, locale);
  const segments = rentalSegments(driver, places, window, locale);
  if (!places && !window) return null;
  const who = driver?.name ?? copy.driverPlaceholder;
  return (
    <SummaryPill
      segments={segments}
      // No celular, lugar · dias em cima e horários · condutor embaixo (#2660).
      compact={{
        primary: places,
        when: window?.days,
        secondary: [window?.hours, who].filter(Boolean).join(' · '),
      }}
      ariaLabel={copy.aria}
      {...(readOnly !== undefined ? { readOnly } : {})}
      {...(onExpand ? { onExpand } : {})}
      {...(search ? { search } : {})}
    />
  );
}
